/* eslint-disable no-param-reassign */
import { makeVar } from '@apollo/client';

import { initLiquidEngine } from '@/lib/liquidEngine';
import { SitePageQueryData } from '@/queries/SitePage';
import type { DraftSection } from '@/types/DraftSection';
import { GlobalStyles } from '@/types/GlobalStyles';
import { SectionSettings } from '@/types/SectionSettings';
import type { SitePage } from '@/types/SitePage';
import { SiteSection } from '@/types/SiteSection';
import { SiteTemplate } from '@/types/SiteTemplate';
import {
  DATA_ATTRIBUTE_SECTION_LIST,
  GLOBAL_STYLE_PRESETS,
  MessageAction,
  Preset,
  SECTION_BACKGROUND_TYPE,
} from '@/utils/constants';
import { type PaletteSource } from '@/utils/globalThemesTrackingHelpers';
import {
  determinePreset,
  postMessageToParent,
  savePreset,
} from '@/utils/presetHelper';

export const allSiteTemplatesVar = makeVar<SiteTemplate[]>([]);
export const sitePageVar = makeVar<SitePage | null>(null);
export const globalStylesVar = makeVar<GlobalStyles | null>(null);
export const initialGlobalStylesVar = makeVar<GlobalStyles | null>(null);
export const colorPaletteSourceVar = makeVar<PaletteSource | null>(null);
export const colorPaletteSwatchVar = makeVar<keyof GlobalStyles | null>(null);
export const siteSectionsCopyVar: any = makeVar({});
export const hasUnsavedChangesVar = makeVar<boolean>(false);
export const templatePageVar = makeVar<SitePageQueryData['sitePage']>(null);
export const trackingKeyVar = makeVar<string | null>(null);
export const globalStylesMenuOpenVar = makeVar<boolean>(false);
export const disableEditingVar = makeVar<boolean>(false);

// Keep track of sections to be removed on next save.
export const sectionsToRemove = makeVar<(string | number)[]>([]);

// When set, reveals the `SectionSettingsSidebar`
export const sidebarSectionVar = makeVar<SiteSection | null>(null);

export const getSiteTemplate = (siteTemplateId: string) => {
  const siteTemplate = allSiteTemplatesVar().find(
    (st) => st.id === siteTemplateId
  );

  if (!siteTemplate) {
    throw new Error('Unable to find site template from id');
  }

  return siteTemplate;
};

export const getAllSiteTemplateLayouts = (siteTemplateId: string) => {
  const siteTemplate = getSiteTemplate(siteTemplateId);
  return siteTemplate.currentVersion.templateLayouts.nodes;
};

export const getSiteTemplateVersion = (siteTemplateId: string) => {
  const siteTemplate = getSiteTemplate(siteTemplateId);
  return siteTemplate.currentVersion;
};

export const getSiteLayout = () => {
  const sitePage = sitePageVar();
  return sitePage?.siteLayout;
};

export const getSiteTemplateLayout = () => {
  const siteLayout = getSiteLayout();
  if (!siteLayout) {
    throw new Error('No site layout found');
  }

  const { siteTemplate, templateIdentifier } = siteLayout;
  const siteTemplateVersion = getSiteTemplateVersion(siteTemplate.id);

  return siteTemplateVersion.templateLayouts.nodes.find(
    (s) => s.identifier === templateIdentifier
  );
};

export const getAllSiteTemplateSections = (siteTemplateId: string) => {
  const siteTemplateVersion = getSiteTemplateVersion(siteTemplateId);
  const siteTemplateSections = siteTemplateVersion.templateSections.nodes;

  return siteTemplateSections;
};

export const getSiteTemplateSection = (
  siteTemplateId: string,
  identifier: string
) => {
  const siteTemplateSections = getAllSiteTemplateSections(siteTemplateId);

  return siteTemplateSections.find(
    (section) => section.identifier === identifier
  );
};

export const getSitePageSections = (targetKey?: string) => {
  const sitePage = sitePageVar();
  if (!sitePage || !sitePage.siteSections) {
    throw new Error('No site page with sections found');
  }

  let sortedSiteSections = sitePage.siteSections.nodes;

  if (targetKey) {
    sortedSiteSections = sortedSiteSections.filter(
      (s) => s.targetKey === targetKey
    );
  }

  return sortedSiteSections.sort(
    (sectionA, sectionB) =>
      (sectionA.positionDraft ?? 0) - (sectionB.positionDraft ?? 0)
  );
};

export const getSiteLayoutSections = (targetKey?: string) => {
  const sitePage = sitePageVar();
  if (!sitePage || !sitePage.siteLayout?.siteSections) {
    throw new Error('No site page or layout with sections found');
  }

  let sortedSiteSections = sitePage.siteLayout.siteSections.nodes;

  if (targetKey) {
    sortedSiteSections = sortedSiteSections.filter(
      (s) => s.targetKey === targetKey
    );
  }

  return sortedSiteSections.sort(
    (sectionA, sectionB) =>
      (sectionA.positionDraft ?? 0) - (sectionB.positionDraft ?? 0)
  );
};

export const getLiquidEngine = (siteTemplateId: string) => {
  const siteTemplateVersion = getSiteTemplateVersion(siteTemplateId);
  return initLiquidEngine(siteTemplateVersion);
};

export const blockSettingsForLiquid = (blockSetting: any) => {
  const { blocks } = blockSetting;
  return blocks.map((block: any) => ({
    block_type: block.blockType,
    settings: settingsForLiquid(block.settings),
  }));
};

export const settingsForLiquid = (rawSettings: any[]) => {
  const settings: { [key: string]: any } = {};

  rawSettings.forEach((setting: any) => {
    if (setting.type === 'blockList') {
      // BlockLists are a unique shape & require special handling.
      settings[setting.name] = blockSettingsForLiquid(setting);
    } else {
      settings[setting.name] = setting[setting.valueType];
    }
  });

  return settings;
};

export const snakeToCamelCase = (value: string) =>
  value.replace(/[-_][a-z]/gi, (group) =>
    group.toUpperCase().replace(/[-_]/g, '')
  );

export const snakeCase = (value: string): string =>
  value.replace(/[A-Z]/g, (letter: string) => `_${letter.toLowerCase()}`);

export const underscoreAllKeys = (value: any): object | string => {
  switch (value?.constructor.name) {
    case 'Object':
      return Object.keys(value).reduce(
        (destination, key) => ({
          ...destination,
          [snakeCase(key)]: underscoreAllKeys(value[key]),
        }),
        {}
      );
    case 'Array':
      return value.map((i: any) => underscoreAllKeys(i));
    default:
      return value;
  }
};

export const getSectionsForSavingDraft = (): DraftSection[] => {
  const siteSections = getSitePageSections();
  const layoutSections = getSiteLayoutSections();

  const draftSections = [...siteSections, ...layoutSections].map((section) => {
    const siteTemplateSectionSchema = getSiteTemplateSection(
      section.siteTemplate.id,
      section.templateIdentifier
    )?.schema;

    return {
      id: section.id.startsWith('new-section-') ? -1 : section.id,
      identifier: section.templateIdentifier,
      remove: sectionsToRemove().includes(section.id),
      newPosition: section.positionDraft,
      newSettings: section.settingsDraft.reduce(
        (allSettings: any, setting: any) => {
          const { valueType } = setting;

          if (valueType === 'blockList') {
            allSettings[setting.name] = {
              value: setting.blocks.map((block: any) => ({
                blockType: block.blockType,
                settings: block.settings.reduce(
                  (allBlockSettings: any, blockSetting: any) => {
                    const blockTypeSchema =
                      siteTemplateSectionSchema?.blockTypes?.[block.blockType];

                    if (!blockTypeSchema) {
                      return allBlockSettings;
                    }

                    const { valueType: blockSettingValueType } = blockSetting;
                    const settingIsDefinedByBlockTypeSchema =
                      blockTypeSchema.settings.some(
                        (s: any) => s.id === blockSetting.name
                      );

                    // Extra settings can be present when switching between block types.
                    // Only include the setting while saving it is defined by the block type's schema.
                    if (settingIsDefinedByBlockTypeSchema) {
                      allBlockSettings[blockSetting.name] = {
                        type: blockSetting.type,
                        value: blockSetting[blockSettingValueType],
                      };
                    }

                    return allBlockSettings;
                  },
                  {}
                ),
              })),
            };
          } else if (valueType === SECTION_BACKGROUND_TYPE) {
            allSettings[setting.name] = {
              type: setting.type,
              value: {
                ...setting[valueType],
                __typename: undefined,
              },
            };
          } else {
            allSettings[setting.name] = {
              type: setting.type,
              value: setting[valueType],
            };
          }

          return allSettings;
        },
        {}
      ),
    };
  });

  return draftSections;
};

export const updateSiteSection = (updateSection: SiteSection) => {
  const sitePage = sitePageVar();
  if (!sitePage) {
    throw new Error('Site page not found');
  }

  const sitePageCopy = { ...sitePage };
  const pageSections = [...(sitePageCopy.siteSections?.nodes ?? [])];
  const layoutSections = [
    ...(sitePageCopy.siteLayout?.siteSections?.nodes ?? []),
  ];

  const pageSectionIndex = pageSections.findIndex(
    (section) => section.id === updateSection.id
  );
  if (pageSectionIndex > -1) {
    pageSections[pageSectionIndex] = updateSection;
    sitePageCopy.siteSections = {
      ...sitePageCopy.siteSections!,
      nodes: [...pageSections],
    };
  }

  const layoutSectionIndex = layoutSections.findIndex(
    (section) => section.id === updateSection.id
  );
  if (layoutSectionIndex > -1 && sitePageCopy.siteLayout) {
    layoutSections[layoutSectionIndex] = updateSection;
    sitePageCopy.siteLayout.siteSections = {
      ...sitePageCopy.siteLayout.siteSections!,
      nodes: [...layoutSections],
    };
  }

  sitePageVar(sitePageCopy);
};

export const renderPageHtml = async () => {
  const sitePage = sitePageVar();
  if (!sitePage) {
    throw new Error('No site page found');
  }

  const siteTemplatePage = getSiteTemplatePage();
  if (!siteTemplatePage) {
    throw new Error('No SiteTemplatePage found');
  }

  const liquidEngine = getLiquidEngine(sitePage.siteTemplate.id);

  const { schema } = siteTemplatePage;
  const { sectionsVariable, sectionPortalDefinitions } = renderSectionTargets({
    forSitePage: true,
    sitePageOrLayout: sitePage,
    schema,
  });

  const pageHtml = await liquidEngine.parseAndRender(
    siteTemplatePage.liquidMarkup || '',
    {
      sections: sectionsVariable,
    }
  );

  return {
    pageHtml: pageHtml.trim(),
    pagePortalDefinitions: sectionPortalDefinitions,
  };
};

export const renderLayoutHtml = async (pageHtml: HTMLElement) => {
  const sitePage = sitePageVar();
  if (!sitePage) {
    throw new Error('No site page found');
  }

  const siteTemplateVersion = getSiteTemplateVersion(sitePage.siteTemplate.id);
  const siteLayout = getSiteLayout();
  if (!siteLayout) {
    throw new Error('No site layout found');
  }
  const siteTemplateLayout = getSiteTemplateLayout();
  if (!siteTemplateLayout) {
    throw new Error('No site template layout found');
  }

  const liquidEngine = getLiquidEngine(siteLayout.siteTemplate.id);

  const { schema } = siteTemplateLayout;
  const { sectionsVariable, sectionPortalDefinitions } = renderSectionTargets({
    forSitePage: false,
    sitePageOrLayout: siteLayout,
    schema,
  });

  const layoutParts = [
    siteTemplateLayout.liquidMarkup || '',
    siteTemplateVersion?.liquidMarkupForFooter || '',
  ];

  const parsedHead = await liquidEngine.parseAndRender(
    siteTemplateVersion?.liquidMarkupForHead || ''
  );

  const layoutHtml = await liquidEngine.parseAndRender(layoutParts.join(''), {
    sections: sectionsVariable,
    content_for_layout: pageHtml,
  });

  return {
    headerHtml: parsedHead,
    layoutHtml: layoutHtml.trim(),
    layoutPortalDefinitions: sectionPortalDefinitions,
  };
};

type RenderSectionTargetsParams = {
  forSitePage: boolean;
  sitePageOrLayout: any;
  schema: any;
};

export const renderSectionTargets = ({
  forSitePage,
  sitePageOrLayout,
  schema,
}: RenderSectionTargetsParams) => {
  const sectionTargetKeys = Object.keys(schema?.sections || {});

  const sectionsVariable: { [key: string]: string } = {};
  const sectionPortalDefinitions: any[] = [];

  sectionTargetKeys.forEach((targetKey) => {
    const domDataAttribute = `${sitePageOrLayout.id}-${targetKey}`;

    sectionsVariable[
      targetKey
    ] = `<div data-qa="section-target" ${DATA_ATTRIBUTE_SECTION_LIST}="${domDataAttribute}"></div>`;

    sectionPortalDefinitions.push({
      domDataAttribute,
      forSitePage,
      targetKey,
    });
  });

  return {
    sectionsVariable,
    sectionPortalDefinitions,
  };
};

export const handleMessage = (event: MessageEvent) => {
  if (!event.isTrusted) {
    return;
  }

  const { type, payload, source, navigateTo, sectionStyle } = event.data;

  // ignore messages being sent to Rails with this source to support empty payload for Rails
  if (source === 'croissant') {
    return;
  }

  const preset = determinePreset(payload?.preset);

  switch (type as MessageAction) {
    case MessageAction.PageSaved:
      sitePageVar(payload.sitePage);
      break;
    case MessageAction.PreviewLoaded:
      allSiteTemplatesVar(payload.siteTemplates);
      sitePageVar(payload.sitePage);
      globalStylesVar(payload.globalStyles);
      generateLocalCopySections();
      break;
    case MessageAction.SectionAdded:
      sitePageVar(payload.sitePage);
      generateLocalCopySections();
      hasUnsavedChangesVar(true);
      break;
    case MessageAction.SectionUpdated:
      updateSiteSection(payload.section);
      if (source === 'preview') {
        checkForUnsavedChanges(payload.section, payload.settings);
      }
      break;
    case MessageAction.SectionRemoved:
      if (source === 'preview') {
        removeSection(payload.section, 'parent');
        hasUnsavedChangesVar(true);
      }
      break;
    case MessageAction.SidebarSectionUpdated:
      sidebarSectionVar(payload?.section);
      break;
    case MessageAction.StylesUpdated:
      globalStylesVar(payload.globalStyles);
      break;
    case MessageAction.SiteLayoutUpdated:
      // This actually will make you lose any unsaved changes before you reload the iframe
      window.location.reload();
      break;
    case MessageAction.PresetLoaded:
      applyPresetGlobalStyles(preset);
      applyPresetPageSettings(preset);
      // Communicate back to Rails that the preset preview is loaded
      postMessageToParent(MessageAction.PreviewLoaded, {});
      break;
    case MessageAction.PresetSaved:
      savePreset(
        preset,
        globalStylesVar(),
        getSectionsForSavingDraft(),
        sitePageVar()!.id
      );
      break;
    case MessageAction.OpenGlobalStylesMenu:
      // Message from the preview window to the parent window
      if (source === 'preview') {
        globalStylesMenuOpenVar(true);
        // Handle navigation if navigateTo is provided
        if (navigateTo) {
          window.location.hash = navigateTo;
        }
        if (sectionStyle) {
          colorPaletteSwatchVar(sectionStyle);
        }
      }
      break;
    case MessageAction.DisablePreviewEditing:
      // Message from the parent window to the preview window to disable editing if menu is open
      if (source === 'parent') {
        disableEditingVar(payload?.disableEditing);
      }
      break;
    default:
      break;
  }
};

export const updateParentWindow = (type: MessageAction, payload: any) => {
  if (payload && payload.isPreviewingPreset) {
    window.frames.postMessage(
      {
        type,
        payload,
        source: 'preview',
      },
      window.location.origin
    );
  } else {
    window.parent.postMessage(
      {
        type,
        payload,
        source: 'preview',
      },
      window.location.origin
    );
  }
};

export const updatePreviewWindow = (type: MessageAction, payload: any) => {
  (
    document.querySelector('#preview-iframe') as HTMLIFrameElement
  )?.contentWindow?.postMessage(
    {
      type,
      payload,
      source: 'parent',
    },
    window.location.origin
  );
};

export const generateLocalCopySections = () => {
  const sitePage = sitePageVar();
  if (!sitePage || !sitePage.siteLayout) {
    throw new Error('No site page or layout found');
  }

  const { siteSections, siteLayout } = sitePage;
  const { siteSections: siteLayoutSections } = siteLayout;

  const sectionsCopy: any = {};
  [
    ...(siteSections?.nodes ?? []),
    ...(siteLayoutSections?.nodes ?? []),
  ].forEach((item: any) => {
    sectionsCopy[item.id] = {
      positionDraft: item.positionDraft,
      settingsDraft: [...item.settingsDraft],
    };
  });

  sectionsToRemove([]);
  siteSectionsCopyVar(sectionsCopy);
};

export const checkForUnsavedChanges = (
  siteSection: SiteSection,
  currentSettings: any
) => {
  const sectionsLocalCopy = siteSectionsCopyVar();
  const originalPosition = sectionsLocalCopy[siteSection.id].positionDraft;
  const originalSettings = sectionsLocalCopy[siteSection.id].settingsDraft;

  const isPositionPristine = originalPosition === siteSection.positionDraft;
  const areSettingsPristine = originalSettings.every(
    (item: any, index: number) => {
      if (item.valueType === 'blockList') {
        return item.blocks === currentSettings[index].blocks;
      }
      if (item.valueType === SECTION_BACKGROUND_TYPE) {
        return (
          JSON.stringify(item.background) ===
          JSON.stringify(currentSettings[index]?.background)
        );
      }

      return item[item.valueType] === currentSettings[index][item.valueType];
    }
  );

  const isPristine = areSettingsPristine && isPositionPristine;
  hasUnsavedChangesVar(!isPristine);
};

export const parseContentToPlainText = (
  editableElement: Element | null
): string => {
  if (!editableElement) {
    return '';
  }

  let newValue = '';
  let isOnFreshLine = true;

  const parseChildNodesForValueAndLines = (
    // eslint-disable-next-line no-undef
    childNodes: NodeListOf<ChildNode>
  ) => {
    for (let i = 0; i < childNodes.length; i += 1) {
      const childNode = childNodes[i];

      if (childNode.nodeName === 'BR') {
        // BRs are always line breaks which means the next loop is on a fresh line
        newValue += '\n';
        isOnFreshLine = true;
        // eslint-disable-next-line no-continue
        continue;
      }

      // We may or may not need to create a new line
      if (childNode.nodeName === 'DIV' && isOnFreshLine === false) {
        // Divs create new lines for themselves if they aren't already on one
        newValue += '\n';
      }

      // Whether we created a new line or not, we'll use it for this content so the next loop will not be on a fresh line:
      isOnFreshLine = false;

      // Add the text content if this is a text node:
      if (childNode.nodeType === 3 && childNode.textContent) {
        newValue += childNode.textContent;
      }

      // If this node has children, get into them as well:
      parseChildNodesForValueAndLines(childNode.childNodes);
    }
  };

  parseChildNodesForValueAndLines(editableElement.childNodes);

  // Different browsers treat contenteditables quite differently, and occassionally
  // will include a stray `\n` at the end of the content. Even-number newlines are
  // valid and represent breaking to a new line entirely. Odd-number newlines should
  // be trimmed for consistency as they have no effect on their own.
  const trimOddNewlines = (str: string) => {
    // Count the number of newline characters at the end
    const newlineCount = (str.match(/\n+$/g) || [''])[0].length;

    // If the newline count is odd, trim one newline character
    if (newlineCount % 2 === 1) {
      return str.slice(0, -1);
    }

    // If the newline count is even or zero, return the original string
    return str;
  };

  // Run `newValue` through DOMParser to ensure only text content is preserved.
  const doc = new DOMParser().parseFromString(
    trimOddNewlines(newValue),
    'text/html'
  );

  return doc.body.textContent || '';
};

export const parseStringToPlainText = (value: string) => {
  const newString = new DOMParser().parseFromString(value, 'text/html');

  return newString.body.textContent;
};

export const getSiteTemplatePage = () => {
  const sitePage = sitePageVar();
  if (!sitePage) {
    throw new Error('No site page found');
  }

  const { siteTemplate, templateIdentifier } = sitePage;
  const siteTemplateVersion = getSiteTemplateVersion(siteTemplate.id);
  const siteTemplatePages = siteTemplateVersion.templatePages.nodes;

  return siteTemplatePages.find(
    (page) => page.identifier === templateIdentifier
  );
};

export const getSettingByType = (section: SiteSection, settingType: string) => {
  const draftSettings = section.settingsDraft;
  const currentSetting = draftSettings.find(
    (setting: { type: string }) => setting.type === settingType
  );

  return currentSetting;
};

export const updateSectionPosition = (
  section: SiteSection,
  direction: 'up' | 'down'
) => {
  const sitePage = sitePageVar();
  if (!sitePage) {
    throw new Error('SitePage is not set');
  }

  const pageSections = sitePage.siteSections?.nodes || [];

  // Create a copy of the current page sections.
  const updatedSections = JSON.parse(JSON.stringify(pageSections)) || [];

  // Calculate the current and next positions of the action.
  const currentPosition = section.positionDraft!;
  const nextPosition =
    direction === 'up' ? currentPosition - 1 : currentPosition + 1;

  // Ensure `nextPosition` is in range
  if (nextPosition < 0 || nextPosition >= pageSections.length) {
    return;
  }

  // Find indexes of the sections that will be swapping positions
  const originalSectionIndex = pageSections.findIndex(
    (s) => s.id === section.id
  );
  const sectionAtTargetPositionIndex = pageSections.findIndex(
    (s) => s.positionDraft === nextPosition
  );

  // Update their positions.
  updatedSections[originalSectionIndex].positionDraft = nextPosition;
  updatedSections[sectionAtTargetPositionIndex].positionDraft = currentPosition;

  // Update the `sitePageVar` representation.
  sitePageVar({
    ...sitePage,
    siteSections: {
      ...sitePage.siteSections,
      nodes: updatedSections,
    },
  });

  // Notify the parent window of the 2 section changes.
  updateParentWindow(MessageAction.SectionUpdated, {
    section: updatedSections[sectionAtTargetPositionIndex],
    settings: updatedSections[sectionAtTargetPositionIndex].settingsDraft,
  });

  updateParentWindow(MessageAction.SectionUpdated, {
    section: updatedSections[originalSectionIndex],
    settings: updatedSections[originalSectionIndex].settingsDraft,
  });
};

export const removeSection = (
  section: SiteSection,
  source: 'preview' | 'parent'
) => {
  const sitePage = sitePageVar();
  if (!sitePage) {
    throw new Error('SitePage is not set');
  }

  const pageSections = sitePage.siteSections?.nodes || [];

  // Create a copy of the current page sections.
  const updatedSections = JSON.parse(JSON.stringify(pageSections)) || [];

  // Find the index of the section being removed.
  const sectionIndex = pageSections.findIndex((s) => s.id === section.id);

  if (source === 'preview') {
    // Remove the section from the Preview window's `sitePageVar().siteSections`
    // instance so it is not rendered again by the preview window.
    updatedSections.splice(sectionIndex, 1);
  } else {
    // Append the section ID to the `sectionsToRemove` variable so that it is still
    // present in `sitePageVar().siteSections`, but will be marked for removal on next save.
    sectionsToRemove().push(section.id);
  }

  // Decrement the `positionDraft` of sections that had positions greater than the removed section.
  updatedSections.forEach((s: SiteSection) => {
    if (s.positionDraft! > section.positionDraft!) {
      s.positionDraft = s.positionDraft! - 1;
    }
  });

  // Update the `sitePageVar` representation.
  sitePageVar({
    ...sitePage,
    siteSections: {
      ...sitePage.siteSections,
      nodes: updatedSections,
    },
  });

  // Notify the parent window of the section removal when triggered from the preview window.
  if (source === 'preview') {
    updateParentWindow(MessageAction.SectionRemoved, {
      section,
    });
  }
};

export const getValueType = (settingType: string) => {
  const typeMap: { [key: string]: string } = {
    background: 'background',
    blockList: 'blocks',
    category: 'collection',
    course: 'course',
    courseList: 'courseList',
    courseReview: 'courseReview',
    courseReviewList: 'courseReviewList',
    checkbox: 'boolean',
    date: 'date',
    datetime: 'datetime',
    instructor: 'instructor',
    number: 'float',
    range: 'float',
    productPrice: 'productPrice',
    productPriceList: 'productPriceList',
    product: 'product',
    productList: 'productList',
    color: 'string',
    icon: 'string',
    imageOrigin: 'string',
    imagePosition: 'string',
    image: 'string',
    layout: 'string',
    pageUrl: 'string',
    richtext: 'string',
    text: 'string',
    textarea: 'string',
    radio: 'string',
    select: 'string',
    video: 'video',
  };

  return typeMap[settingType];
};

export const addNewSection = (newSection: SiteSection) => {
  const sitePage = sitePageVar();
  if (!sitePage) {
    throw new Error('SitePage is not set');
  }

  const pageSections = sitePage.siteSections?.nodes || [];

  // Create a copy of the current page sections.
  const updatedSections = JSON.parse(JSON.stringify(pageSections)) || [];

  // Adjust the positions of the existing sections after the insert position.
  updatedSections.forEach((section: SiteSection) => {
    if (section.positionDraft! >= newSection.positionDraft!) {
      section.positionDraft! += 1;
    }
  });

  // Insert the new section at the specified position.
  updatedSections.splice(newSection.positionDraft!, 0, newSection);

  // Update the `sitePageVar` representation with the new section list.
  sitePageVar({
    ...sitePage,
    siteSections: {
      ...sitePage.siteSections,
      nodes: updatedSections,
    },
  });

  // Notify the parent window of the new section addition.
  updateParentWindow(MessageAction.SectionAdded, {
    sitePage: sitePageVar(),
  });
};

export const updateSectionSettings = (
  sectionToUpdate: SiteSection,
  newSettings: SectionSettings,
  deferred = false
): SiteSection => {
  const settings = [...sectionToUpdate.settingsDraft];
  let newSection = sectionToUpdate;

  // eslint-disable-next-line no-restricted-syntax
  for (const [key, value] of Object.entries(newSettings)) {
    const index = settings.findIndex((s) => s.name === key);
    const setting = { ...settings[index] };
    const valueType = getValueType(setting.type);

    if (JSON.stringify(setting[valueType]) !== JSON.stringify(value)) {
      setting[valueType] = value;
      settings[index] = setting;
      newSection = { ...newSection, settingsDraft: settings };
    }
  }

  if (sidebarSectionVar()?.id === newSection.id) {
    sidebarSectionVar(newSection);
    updateParentWindow(MessageAction.SidebarSectionUpdated, {
      section: newSection,
    });
  }

  updateSiteSection(newSection);

  if (!deferred) {
    updateParentWindow(MessageAction.SectionUpdated, {
      section: newSection,
      settings,
    });
  }
  return newSection;
};

// reset a section back to its settings when first loaded
export const resetSectionSettings = (
  sectionToReset: SiteSection
): SiteSection | null => {
  // original sections we can swap back to
  const originalSiteSections = siteSectionsCopyVar();
  if (!originalSiteSections) {
    throw new Error('SiteSectionsCopy is not set');
  }

  const sectionDefaultSettings = originalSiteSections[sectionToReset.id];
  if (!sectionDefaultSettings) {
    return null;
  }

  const sectionCopy = sectionToReset;
  sectionCopy.settingsDraft = sectionDefaultSettings.settingsDraft;

  if (sidebarSectionVar()?.id === sectionCopy.id) {
    sidebarSectionVar(sectionCopy);
  }
  updateSiteSection(sectionCopy);
  return sectionCopy;
};

export const applyPresetGlobalStyles = (preset: Preset) => {
  globalStylesVar({
    ...globalStylesVar()!,
    ...GLOBAL_STYLE_PRESETS[preset],
  });
};

const applyPresetPageSettings = (preset: Preset) => {
  // Get page schema
  const sitePage = sitePageVar();
  if (!sitePage) {
    throw new Error('SitePage is not set');
  }

  // Get the initialSections list from the page schema
  const initialSections =
    getSiteTemplatePage()?.schema?.sections?.main?.initialSections;

  // Iterate over each section, and apply any preset settings if defined
  // or reset back to default if requested
  // eslint-disable-next-line no-restricted-syntax
  for (const initialSection of initialSections) {
    if (
      (!initialSection.presetSettings ||
        !initialSection.presetSettings[preset]) &&
      preset !== 'default'
    ) {
      // eslint-disable-next-line no-continue
      continue;
    }

    const allPageSections = [
      ...(sitePage.siteSections?.nodes ?? []),
      ...(sitePage.siteLayout?.siteSections.nodes ?? []),
    ];

    const sectionId = sitePage.siteSections?.nodes.find(
      (section) => section.templateIdentifier === initialSection.identifier
    )?.id;

    const currentSection = allPageSections.find(
      (item: any) => item.id === sectionId
    );

    if (currentSection) {
      if (preset === 'default') {
        resetSectionSettings(currentSection);
      } else {
        updateSectionSettings(
          currentSection,
          initialSection.presetSettings[preset],
          false
        );
      }
    }
  }
};

export const previewPreset = (preset: Preset) => {
  updatePreviewWindow(MessageAction.PresetLoaded, {
    preset,
  });
};
